"use client";

/**
 * The reading surface for "which of my resources are fighting, and which copy
 * wins".
 *
 * `src/lib/gtaKnowledge/clash.ts` does the whole judgement and this file does
 * none of it. It finds no collision, decides no winner, compares no bytes and
 * ranks nothing: `findClashes` returns a `ClashReport` whose contests are
 * already sorted worst-first with the emergency fleet above everything else,
 * and whose sentences are already written. What is left for a view is the part
 * only a view can get wrong, and the four things below are the four ways a
 * correct library gets quietly undone by the page in front of it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. THE ATTRIBUTION, WHICH IS THE ENTIRE REASON PHASE 0 EXISTS
 *
 * `intake.ts` hands back a unique `root` per resource and an `origins` map
 * keyed by that root, precisely so that two vendor zips that both ship
 * `police/` survive as two resources. A view that passes `collectResources`
 * only `{ name, files }` throws both away at the door: the two packs fold into
 * one, `collectStreamAssets` treats the second pack's `stream/police.yft` as a
 * re-listing of the first's, and this tool reports a clean namespace over the
 * exact input it was opened to diagnose. That is a one-line mistake in a view
 * and it is the competitor's headline defect, so `groupsOf` is a named,
 * exported function with a test of its own rather than three words inline.
 *
 * 2. THE TWO PASSES ARE THE USER'S, NOT OURS
 *
 * Names first: `findClashes` runs on the drop the moment it is read, and every
 * contest is on screen immediately. Bytes second, and only for the files that
 * actually collide — `clashCandidates` is the cheap pass that says which those
 * are, and on a 300-resource drop the hash is the only slow thing on this page.
 *
 * So it is a button, with a count and a size on it, and a Skip beside it. What
 * a skip must never buy is a verdict nobody earned: `resolveContent` answers
 * `unknown` rather than `different` when nothing was hashed, and this page
 * prints that as **not compared** in the same place a verdict would go. The
 * reader is told the check did not run, in the contest, in the caveat line and
 * in the export. A tool that quietly called two uncompared copies "different"
 * would report every vendor's own shared wheel as a live override.
 *
 * 3. A ZIP HAS NO BYTES TO HASH UNTIL THIS PAGE GOES BACK FOR THEM
 *
 * `intake.ts` inflates text entries only. A binary entry inside a dropped zip
 * is carried as a `File` whose `size` is exact — read from the archive's
 * central directory — and whose body is empty, which is what keeps a 4 GB drop
 * as cheap as listing it. That is a property of the *caller*, as `intake.ts`
 * says out loud, and this is the caller: handing that placeholder to
 * `crypto.subtle.digest` hashes zero bytes, two placeholders hash to the same
 * value, and the page prints "identical, harmless" over two files nobody has
 * read. So `hashContestedFiles` compares what it got back against the size the
 * inventory states and treats a short read as *no bytes* rather than as an
 * empty file.
 *
 * But refusing to answer is only half of it, and on the zip path it is the
 * wrong half: two vendor archives is the input the brief names as the scenario
 * this tool exists for, and every contested `.yft` in one arrives bodyless. A
 * page that only says so offers a "Compare the bytes" button that can never
 * produce a verdict, and the byte-identical vendor-shared-part case — the whole
 * reason claim 2 exists — never gets called harmless. So the page keeps the
 * user's original archive handles and, when a contested file has no body,
 * `inflateContested` re-opens those archives with a filter limited to the
 * contested entries and inflates only those. That is the same two-pass economy
 * as the rest of the tool: the first pass reads the central directory and
 * nothing else, and the second inflates a handful of files the user asked for.
 * What it cannot reach — an archive whose namespaced path this page cannot
 * reconstruct, or one over the inflate ceiling — still comes back bodyless,
 * with the sentence saying so.
 *
 * Sizes still settle every zip contest they can before any of this — two
 * different lengths cannot be the same bytes, which `clash.ts` answers without
 * reading anything at all.
 *
 * 4. EVERY COUNT COUNTS EVERY ROW
 *
 * A `resources/` folder holds hundreds of packs and a collision report runs to
 * thousands of contests, so the contest list is drawn a slice at a time. The
 * rule `ResmonReport.tsx` states and this file keeps: a cap changes how many
 * cards are mounted and nothing else. The figures, the search line, the
 * findings panel and all three exports count and carry every row, and the
 * library's own cap — `MAX_CLASHES`, at five thousand — announces itself in
 * `skipped` rather than trimming in silence.
 *
 * The same rule one level up: there is ONE finding set and ONE skipped list on
 * this page, and `exported` is it. `analyzeResourceSet` hangs cross-resource
 * findings off the set and per-resource ones off each resource; this page draws
 * the first and `report.ts` flattens both, so the screen said "1 collision"
 * while the CSV described five findings under a different verdict. And a
 * `truncated` list is a prop of the findings panel, which no exporter can see,
 * so a `.rar` the picker accepted was refused on screen and certified in the
 * Markdown. Both go through `exported`, which is what the three buttons and the
 * panel's own Copy-as-Markdown are handed. See the memo for the argument.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE CONTEST LIST IS BOUNDED AND NOT WINDOWED
 *
 * `VirtualGrid` is the house windowing component and is the wrong shape here
 * for the reason `SpawnIndexClient.tsx` gives about its own list: it lays out a
 * flat grid of fixed-height cards driven by a `ResizeObserver`, and a contest
 * card is a heading, a verdict, a winner paragraph, a caveat list and one row
 * per copy — as tall as its copies and its sentences make it. A measured
 * viewport also decides how much of the answer exists, which is wrong for a
 * page whose output a server owner is going to Ctrl-F through and paste.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT LEAVES THE TAB
 *
 * Nothing of the user's. There is no upload path in this page's import graph.
 * The one request it makes is a `GET` for our own shipped base-game name index,
 * a static 857 KB file, because subtracting the 238,359 stock names is what
 * turns "two files share a name" into a report rather than noise — and a mod
 * that replaces a stock `.ydr` is context, not a fault. When that fetch fails
 * the page says so and the contests lose that one sentence; it never pretends
 * the check ran.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS
 *
 * This renders one scan. Picking again replaces it, and there is deliberately
 * no "add another folder": `collectResources` can only promise unique
 * identities *within one drop*, so unioning two picks is how two packs called
 * `police` become one and the report comes back clean — the defect above,
 * reached from the other end.
 *
 * It does not rename anything. A rename plan is Spawn Rename's job and the
 * `ClashReport` this page holds is the input for it.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { unzipSync, type Unzipped } from "fflate";
import {
  AlertTriangle,
  ChevronRight,
  Download,
  FileCog,
  FileJson,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  FolderOpen,
  Upload,
} from "lucide-react";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { FindingsList } from "@/components/gta/FindingsList";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  steps as clashFinderSteps,
  finishLabel as clashFinderFinishLabel,
} from "@/components/walkthrough/steps/clashFinder";
import { downloadTextFile } from "@/lib/dom";
import {
  DEMO_INPUT_NOTE,
  DEMO_STATE_LABEL,
  demoDownloadName,
  demoPacksFor,
  type DemoPack,
  type DemoPackUse,
} from "@/lib/gtaKnowledge/demoPacks";
import {
  CLASH_FINDER_TOOL,
  CLASH_SCOPE_NOTE,
  LOAD_ORDER_UNKNOWN,
  clashCandidates,
  findClashes,
  hashClashFiles,
  type Clash,
  type ClashCandidate,
  type ClashDigest,
  type ClashKind,
  type ClashReport,
  type ClashVerdict,
} from "@/lib/gtaKnowledge/clash";
import { collectResources, type CollectableGroup } from "@/lib/gtaKnowledge/collect";
import { verdictFor } from "@/lib/gtaKnowledge/findings";
import {
  fromArchives,
  fromDataTransfer,
  fromDirectoryPick,
  type DataTransferLike,
  type ResourceDrop,
} from "@/lib/gtaKnowledge/intake";
import { normalizeName, normalizePath, pathKey } from "@/lib/gtaKnowledge/normalize";
import { toCsv, toJson, toMarkdown } from "@/lib/gtaKnowledge/report";
import { parseServerCfg, type ParsedCfg } from "@/lib/gtaKnowledge/serverCfg";
import type { GtaResourceInput, VanillaAssetNameIndex } from "@/lib/gtaKnowledge/types";
import { loadVanillaAssetNameIndex } from "@/lib/gtaKnowledge/vanillaAssetNames";

// ─── Caps ────────────────────────────────────────────────────────────────────
//
// Exported so the test asserts against the numbers the page uses rather than
// against a second copy of them, which is how a cap gets raised in one place
// and tested in another.

/** Contest cards drawn before the tail is held back. */
export const CLASH_VISIBLE = 20;

/** How many more contest cards one click mounts. Not "all of them". */
export const CLASH_PAGE = 20;

/**
 * Copies listed inside one contest before its own reveal appears.
 *
 * Six, matching `clash.ts`'s `MAX_COPIES_NAMED`, so the rows under a sentence
 * and the resources named *in* that sentence stop at the same place.
 */
export const CLASH_COPIES_VISIBLE = 6;

/** How many more copies one click mounts inside a contest. */
export const CLASH_COPIES_PAGE = 25;

/** Housekeeping rows drawn before that section's own reveal appears. */
export const HOUSEKEEPING_VISIBLE = 10;

/** How many more housekeeping rows one click mounts. */
export const HOUSEKEEPING_PAGE = 50;

/**
 * How often the hash pass re-renders while it runs.
 *
 * The progress figure is read off the read closure, which fires once per
 * contested file, and setting state on every one of five hundred files
 * re-renders a page holding twenty mounted contest cards five hundred times to
 * move a number. The counter itself is exact — the closure counts every file —
 * and this only decides how often the exact number is shown.
 */
const PROGRESS_EVERY = 8;

// ─── Vocabulary ──────────────────────────────────────────────────────────────

/**
 * The verdict, in the words the page puts where a verdict goes.
 *
 * `unknown` is spelled as a statement about the *check* rather than about the
 * files, because that is what it is: nobody compared them. "Unknown" on its own
 * reads as a property of the copies and invites the reader to assume the worst,
 * which is the assumption this whole split exists to stop them making.
 */
const VERDICT_LABELS: Record<ClashVerdict, string> = {
  identical: "identical copies, harmless",
  different: "different files, one is shadowed",
  unknown: "not compared",
};

const KIND_LABELS: Record<ClashKind, string> = {
  stream: "streamed asset name",
  spawn: "vehicle spawn code",
  data_file: "data_file registration",
};

/**
 * What each kind of contest is, once, rather than hovered for on card 40.
 *
 * Twenty words each, for the reason a legend exists at all: three entries at 46
 * words is a wall a reader skims, and the kind they skimmed past is the kind on
 * the card in front of them. Each entry keeps the asymmetry — last wins for a
 * streamed file, first for a spawn code — because that is the single thing a
 * reader of this page most needs and least expects.
 *
 * `stream` also carries what a collision *means*, which used to be a clause in
 * the byte-pass paragraph above the Compare button. It is rewritten into the
 * twenty words rather than appended to them: the legend explains kinds of
 * collision, and "identical copies are harmless, different ones shadow paid
 * content" is the reason the identical-or-different split is worth a second
 * pass at all.
 */
const KIND_MEANINGS: Record<ClashKind, string> = {
  stream:
    "One flat, server-wide namespace: the resource that starts last wins, silently. Identical copies are harmless; different ones shadow paid content.",
  spawn:
    "Registered once per name: a later registration is discarded, so the resource that starts first wins — unlike a streamed file.",
  data_file:
    "Two manifests claiming one name, or two metas defining one row of a merged table. Both can be called handling.meta.",
};

// ─── Small helpers ───────────────────────────────────────────────────────────

function n(value: number): string {
  return value.toLocaleString("en-US");
}

function count(value: number, singular: string, many?: string): string {
  return `${n(value)} ${value === 1 ? singular : (many ?? `${singular}s`)}`;
}

/**
 * A byte figure for the handful of lines that print one.
 *
 * Local for the reason `SpawnIndexClient.tsx` gives about its own: the other
 * formatters in this layer sit inside a 1,500-line log parser or inside
 * `clash.ts` itself as a private function, and importing a module into a public
 * page to borrow nine lines of arithmetic puts that module in the bundle of
 * every visitor who never drops a folder. This formats a display figure and
 * decides no fact.
 */
function formatSize(bytes: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(2)} ${units[unit]}`;
}

/**
 * One copy's address: which resource, and which file inside it.
 *
 * The same identity-plus-path pair `clash.ts` keys a digest on, joined on NUL
 * for the same reason it gives: an identity and a path can each hold a space
 * and a slash, so `"lspd pack"` + `"a.ydr"` must not key the same as `"lspd"` +
 * `"pack a.ydr"` and answer a read with another file's bytes. Its own key
 * function is private, and this map is a different map — resource file to
 * `File` handle, which only the page holds — so this is the key for the page's
 * own lookup rather than a second copy of the analyzer's.
 */
function byteKey(identity: string, path: string): string {
  return `${normalizeName(identity)}\u0000${pathKey(path)}`;
}

// ─── Intake ──────────────────────────────────────────────────────────────────

/**
 * A drop's resources, with the attribution that keeps two packs two packs.
 *
 * `root` and `origin` are passed through, and that is the whole reason this is
 * a function with a name. See point 1 in the header: dropping them is the
 * defect Phase 0 exists to close, and it is invisible on every input except the
 * one this tool is for.
 *
 * Not imported from `SpawnIndexClient.tsx`, which exports an identical
 * function: importing one page's client island to borrow a helper puts that
 * whole island in this page's bundle. When a third tool wants it, it belongs in
 * `collect.ts` beside `CollectableGroup`, which is the module that defines the
 * shape it produces.
 */
export function groupsOf(drop: ResourceDrop): CollectableGroup[] {
  return drop.resources.map((resource) => {
    const origin = drop.origins.get(resource.root);
    return {
      name: resource.name,
      files: resource.files,
      root: resource.root,
      ...(origin !== undefined && origin !== "" ? { origin } : {}),
    };
  });
}

/** What one drop produced, before a single byte has been compared. */
export type ClashScan = {
  /** The resources, with their identities intact. The input to `findClashes`. */
  inputs: GtaResourceInput[];
  /**
   * The `File` behind every dropped path, keyed by `byteKey`.
   *
   * Held by the page and never by the analyzer, which is the split `clash.ts`
   * is built on: `findClashes` is pure and takes the *answers* to byte
   * comparisons, so a 20 MB model never reaches it.
   */
  bytes: Map<string, File>;
  /**
   * The archive handles the gesture supplied, kept for the second pass.
   *
   * Empty for a folder pick that held no zips, which is the normal case and
   * needs none: a picked file already has its bytes. It is the zip path that
   * needs these, because intake deliberately never inflated the binary entries
   * — see point 3 in the header. Held as the user's own `File` objects, which
   * cost nothing until something reads them.
   */
  archives: File[];
  /** Everything intake and the collector could not read, in their own words. */
  notices: string[];
  /** The stock name index, when it loaded. Absent means the check did not run. */
  vanillaNames?: VanillaAssetNameIndex;
  /** Every path the drop held, including the ones no resource claimed. */
  files: number;
  /** Files the collector actually decoded as text, counted off what it returned. */
  opened: number;
  /** What the drop weighs, from the inventory rather than from anything read. */
  totalBytes: number;
};

/**
 * A `ResourceDrop`, read end to end.
 *
 * Exported so a test can drive the whole pipeline — intake, collection,
 * analysis — without a browser gesture, and so the page has exactly one path
 * from a drop to a scan whatever the gesture was.
 *
 * `archives` is whatever the gesture handed in that was an archive, passed
 * straight through. It is the gesture rather than the drop that knows: a
 * `ResourceDrop` is the inventory after intake and holds no `File` for the
 * container, by design.
 */
export async function scanDrop(
  drop: ResourceDrop,
  archives: readonly File[] = [],
): Promise<ClashScan> {
  const { inputs, truncated } = await collectResources(groupsOf(drop));

  const bytes = new Map<string, File>();
  for (const resource of drop.resources) {
    for (const entry of resource.files) {
      bytes.set(byteKey(resource.root, entry.path), entry.file);
    }
  }

  // Only the ones a zip reader can open. `.rar` and `.7z` reach this list
  // because the picker accepts them, and intake has already said in
  // `drop.truncated` that they were not opened — holding them here would be a
  // second, silent failure on the same file.
  const openable = archives.filter((file) => ARCHIVE_NAME.test(file.name));

  const notices = [...drop.truncated, ...truncated];

  // Fetched rather than assumed, and reported rather than quietly skipped. The
  // index is what separates "a mod replaces a stock asset" — normal, and not a
  // fault — from "two packs are fighting", and a report that lost the
  // distinction without saying so is the failure this layer is written against.
  let vanillaNames: VanillaAssetNameIndex | undefined;
  try {
    vanillaNames = await loadVanillaAssetNameIndex();
  } catch (error) {
    notices.push(
      "The base-game name index did not load " +
        `(${error instanceof Error ? error.message : String(error)}), so no collision below says ` +
        "whether the base game owns the contested name as well. A pack that deliberately replaces " +
        "a stock asset and two packs fighting over one name look the same in this run.",
    );
  }

  return {
    inputs,
    bytes,
    archives: openable,
    notices: [...new Set(notices)],
    ...(vanillaNames ? { vanillaNames } : {}),
    files: drop.allPaths.length,
    opened: inputs.reduce((total, input) => total + input.texts.size, 0),
    totalBytes: drop.allPaths.reduce((total, entry) => total + (entry.size || 0), 0),
  };
}

// ─── Pass two: the bytes ─────────────────────────────────────────────────────

/** Archive names this page can re-open. `.rar` and `.7z` are not among them. */
const ARCHIVE_NAME = /\.zip$/i;

/**
 * How deep a zip-inside-a-zip is followed when going back for contested bytes.
 *
 * A downloads folder zipped whole is one level; a vendor bundle of vendor
 * bundles is two. Past that the drop is a curiosity rather than a server, and
 * an unbounded walk over attacker-shaped input is not something a page should
 * do in a user's tab.
 */
const MAX_ARCHIVE_NESTING = 2;

/**
 * Contested bytes inflated in one pass, before hashing anything.
 *
 * The whole of the economy is in the filter. `unzipSync`'s filter callback runs
 * over the central directory and decides entry by entry whether to inflate, so
 * a 4 GB archive costs a directory walk plus the handful of contested files —
 * the same trick `intake.ts` uses for the first pass, applied to a much smaller
 * list.
 *
 * The path arithmetic is the one thing here that leans on an intake internal,
 * so it is worth stating rather than inferring: intake's rule 2 namespaces an
 * archive's contents by prefixing every entry with the archive's own
 * drop-relative path, which is what keeps two vendor zips of one layout apart.
 * So a contested file's drop path is the archive's path, a slash, and the
 * entry's own name — and the entry to inflate is that drop path with the
 * archive's prefix removed. A candidate whose drop path does not start with any
 * archive's path is simply not found here and stays bodyless: intake renames a
 * second archive of the same name, and guessing at the rename would be reading
 * a file and calling it another one's bytes.
 */
async function inflateContested(
  archives: readonly File[],
  dropPaths: ReadonlySet<string>,
): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  if (archives.length === 0 || dropPaths.size === 0) return out;

  for (const archive of archives) {
    const relative = (archive as { webkitRelativePath?: string }).webkitRelativePath;
    const base = dropRelative(relative && relative !== "" ? relative : archive.name);
    /** Entry name inside this archive → the drop path that wants it. */
    const wanted = new Map<string, string>();
    for (const dropPath of dropPaths) {
      if (dropPath.startsWith(`${base}/`)) wanted.set(dropPath.slice(base.length + 1), dropPath);
    }
    if (wanted.size === 0) continue;

    let data: Uint8Array;
    try {
      data = new Uint8Array(await archive.arrayBuffer());
    } catch {
      continue;
    }
    walkArchive(data, "", wanted, out, 0);
  }
  return out;
}

function walkArchive(
  data: Uint8Array,
  prefix: string,
  wanted: ReadonlyMap<string, string>,
  out: Map<string, Uint8Array>,
  depth: number,
): void {
  const under = (name: string) => {
    for (const entry of wanted.keys()) if (entry.startsWith(`${name}/`)) return true;
    return false;
  };

  let inflated: Unzipped;
  try {
    inflated = unzipSync(data, {
      filter: (entry) => {
        const name = `${prefix}${dropRelative(entry.name)}`;
        if (wanted.has(name)) return true;
        return depth < MAX_ARCHIVE_NESTING && ARCHIVE_NAME.test(name) && under(name);
      },
    });
  } catch {
    // An LZMA or AES archive throws on the inflate having listed perfectly,
    // exactly as `intake.ts` documents. Nothing is recorded, so every candidate
    // in it stays bodyless and is counted and explained below.
    return;
  }

  for (const [name, body] of Object.entries(inflated)) {
    const full = `${prefix}${dropRelative(name)}`;
    const dropPath = wanted.get(full);
    if (dropPath !== undefined) {
      out.set(dropPath, body);
      continue;
    }
    if (depth < MAX_ARCHIVE_NESTING && ARCHIVE_NAME.test(full)) {
      walkArchive(body, `${full}/`, wanted, out, depth + 1);
    }
  }
}

/** Intake's own spelling of a drop path, which the entry names above have to match. */
function dropRelative(path: string): string {
  return normalizePath(path).replace(/^\/+/, "");
}

/**
 * Whether this handle carries bytes, asked without reading them.
 *
 * `sizedPlaceholder` builds a zip's binary entry as `new File([], name)` with
 * the central directory's size written over the `size` property, so the
 * property lies and the underlying blob does not: a one-byte slice of a
 * placeholder is empty and a one-byte slice of a real file is not. Asked here
 * rather than by reading, because the alternative is reading every contested
 * file twice — once to find out whether it has a body and again to hash it —
 * and the contested set is exactly the set of large files.
 *
 * Both ways of being wrong are safe, which is why a guess is acceptable at all.
 * A false "no body" costs one needless inflate and the hash still prefers the
 * handle's own bytes; a false "has a body" gives the answer this page gave
 * before it went back to the archives at all, which is an honest `null`.
 */
function hasBody(file: File): boolean {
  if (file.size === 0) return true;
  try {
    return file.slice(0, 1).size === 1;
  } catch {
    return true;
  }
}

/**
 * Hash the contested files, from whatever the page is still holding — and go
 * back into the archives for the ones it is not.
 *
 * `hashClashFiles` owns the caps, the abort handling and the missing-`subtle`
 * sentence; this owns the one thing only the page knows, which is where a
 * copy's bytes are and whether it has any.
 *
 * The size check is the first half, and point 3 of the header is its argument:
 * a binary entry from a dropped zip is a `File` with a true size and an empty
 * body, and hashing it would make every pair of uninflated copies "identical,
 * harmless" — a confident clean verdict over two files nobody opened. A short
 * read is therefore never treated as an empty file.
 *
 * The second half is `inflateContested`, and it is what turns the refusal into
 * an answer on the gesture the brief names. Without it the two-vendor-zip drop
 * — the input this tool exists for — offers a button that hashes nothing and a
 * report in which claim 2 never produces a verdict at all. A candidate the
 * archives cannot yield is still `null`, which `clash.ts` words as "we looked
 * and failed", and the count of them comes back so the page can say which
 * gesture would have answered the question.
 */
export async function hashContestedFiles(
  scan: ClashScan,
  candidates: readonly ClashCandidate[],
  options?: { signal?: AbortSignal; onProgress?: (done: number) => void },
): Promise<{ digests: ClashDigest[]; notes: string[]; bodyless: number }> {
  let done = 0;
  let bodyless = 0;

  // Which contested files the page is not holding bytes for, decided before a
  // single inflate: a folder pick needs none of this and pays none of it.
  const missing = new Set<string>();
  for (const candidate of candidates) {
    const file = scan.bytes.get(byteKey(candidate.identity, candidate.path));
    if (file && !hasBody(file)) {
      missing.add(dropRelative(`${candidate.identity}/${candidate.path}`));
    }
  }
  const fromArchive =
    missing.size > 0 && scan.archives.length > 0
      ? await inflateContested(scan.archives, missing)
      : new Map<string, Uint8Array>();

  const { digests, skipped } = await hashClashFiles(
    candidates,
    async (candidate) => {
      done += 1;
      options?.onProgress?.(done);
      const file = scan.bytes.get(byteKey(candidate.identity, candidate.path));
      if (!file) {
        bodyless += 1;
        return null;
      }
      const buffer = await file.arrayBuffer();
      if (buffer.byteLength === file.size) return buffer;

      const inflated = fromArchive.get(dropRelative(`${candidate.identity}/${candidate.path}`));
      if (inflated) return inflated;
      bodyless += 1;
      return null;
    },
    { ...(options?.signal ? { signal: options.signal } : {}) },
  );

  const notes = [...skipped];
  if (bodyless > 0) {
    notes.push(
      `Identical-or-different check: ${bodyless} of ${candidates.length} contested files had no ` +
        "bytes to read in this tab. A zip's binary entries are listed with the exact size the " +
        "archive's central directory states and are never unpacked, and this page goes back into " +
        "the archives only for the contested ones — so a file whose archive could not be re-opened " +
        "here, or whose copy arrived without one, cannot be compared. Those names are still " +
        "reported as collisions, without the identical-or-different split. Extract the packs and " +
        "pick the folder to get it.",
    );
  }

  return { digests, notes, bodyless };
}

/** What the reader gets for skipping the second pass, said where a verdict would go. */
const DECLINED_NOTE =
  "Identical-or-different check: skipped. No contested name below says whether its copies are the " +
  "same file, so every one of them is reported as a collision and none of them is reported as " +
  "harmless. Run the byte comparison to get that split.";

// ─── The page ────────────────────────────────────────────────────────────────

type Phase =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "ready"; scan: ClashScan }
  | { kind: "failed"; message: string };

type BytePass =
  | { kind: "not-run" }
  | { kind: "running"; done: number; total: number }
  | { kind: "done"; digests: ClashDigest[]; notes: string[]; hashed: number; total: number }
  | { kind: "declined" };

export function ClashFinderClient() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [bytePass, setBytePass] = useState<BytePass>({ kind: "not-run" });
  const [cfgText, setCfgText] = useState("");
  const [cfgName, setCfgName] = useState("server.cfg");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(CLASH_VISIBLE);
  const [copiesShown, setCopiesShown] = useState<Record<string, number>>({});
  const [housekeepingShown, setHousekeepingShown] = useState(HOUSEKEEPING_VISIBLE);
  const [dragging, setDragging] = useState(false);
  /**
   * Whether what is on screen came from a built-in sample pack.
   *
   * Set by `begin`, which every gesture goes through, so real input clears it
   * without anything having to remember to: the flag is an argument of the read,
   * not a mode the page can be left in. While it is set, `DEMO_INPUT_NOTE` rides
   * the same notice set the scope note does — on screen and inside all three
   * exports — every download is renamed, and the result carries a badge.
   */
  const [demo, setDemo] = useState(false);
  /**
   * The tour's open channel. Raised by the demo button and by nothing else.
   *
   * Never raised when a folder is dropped: a dimmer over a real user's own
   * results is an interruption, and by the empty-state rule the demo buttons
   * that would have explained it are already gone by then.
   */
  const [tourSignal, setTourSignal] = useState(0);
  const intakeAbort = useRef<AbortController | null>(null);
  const hashAbort = useRef<AbortController | null>(null);

  const scan = phase.kind === "ready" ? phase.scan : null;

  const begin = useCallback(
    async (
      read: (signal: AbortSignal) => Promise<ResourceDrop>,
      archives: readonly File[] = [],
      fromDemo = false,
    ) => {
    intakeAbort.current?.abort();
    hashAbort.current?.abort();
    hashAbort.current = null;
    const controller = new AbortController();
    intakeAbort.current = controller;
    setPhase({ kind: "reading" });
    setDemo(fromDemo);
    setBytePass({ kind: "not-run" });
    setQuery("");
    setShown(CLASH_VISIBLE);
    setCopiesShown({});
    setHousekeepingShown(HOUSEKEEPING_VISIBLE);
    try {
      const next = await scanDrop(await read(controller.signal), archives);
      if (controller.signal.aborted) return;
      setPhase({ kind: "ready", scan: next });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPhase({
        kind: "failed",
        message: error instanceof Error ? error.message : String(error),
      });
    }
    },
    [],
  );

  // ── The config, parsed once ───────────────────────────────────────────────
  //
  // `null` rather than an empty `ParsedCfg` when nothing was supplied, because
  // those are different answers: `clash.ts` names a winner only where a config
  // justifies one, and an empty parse would be a config that starts nothing —
  // which is a *finding* about a real config rather than the absence of one.
  const cfg = useMemo<ParsedCfg | null>(
    () => (cfgText.trim() === "" ? null : parseServerCfg(cfgText, { file: cfgName })),
    [cfgText, cfgName],
  );

  // ── The report ────────────────────────────────────────────────────────────

  const candidates = useMemo(
    () => (scan ? clashCandidates(scan.inputs) : ([] as ClashCandidate[])),
    [scan],
  );
  const contestedBytes = candidates.reduce((total, candidate) => total + candidate.size, 0);

  const report = useMemo<ClashReport | null>(() => {
    if (!scan) return null;
    const hashNotes =
      bytePass.kind === "done"
        ? bytePass.notes
        : bytePass.kind === "declined"
          ? [DECLINED_NOTE]
          : [];
    return findClashes(scan.inputs, {
      cfg,
      ...(scan.vanillaNames ? { vanillaNames: scan.vanillaNames } : {}),
      ...(bytePass.kind === "done" ? { digests: bytePass.digests } : {}),
      hashNotes,
    });
  }, [scan, cfg, bytePass]);

  /**
   * The report as it is exported, and as the findings panel reads it.
   *
   * Two changes to what `findClashes` hands back, and both of them are the same
   * argument: a document a server owner pastes into a vendor ticket has to
   * describe the scan they just read, in what it lists and in what it says it
   * did not look at.
   *
   * WHAT IT SAYS IT DID NOT LOOK AT. The scope note and everything intake and
   * the collector refused go into `skipped`, because `toMarkdown`, `toCsv` and
   * `toJson` all print `skipped` with the summary and it is their only channel —
   * `truncated` is a prop of the panel and reaches no export. Without the
   * notices, a drop holding a `.rar` the picker happily accepted exported a
   * document opening "Verdict: Ready to export, 0 errors" over an archive
   * nothing had opened.
   *
   * WHAT IT LISTS. `resources[].findings` is emptied. `analyzeResourceSet` puts
   * cross-resource findings on the set and per-resource ones on each resource,
   * this page renders the first set only — collisions are what it is for — and
   * `report.ts` flattens both. So the screen said "1 collision" and the CSV
   * described five findings, with `stream.path.duplicate` printed twice because
   * only the page's list is deduped. Emptying them costs the export nothing the
   * screen was showing and costs the panel nothing either: its attribution map
   * is built from per-resource findings, and every finding this page renders is
   * a set-level one that was never in it.
   */
  const exported = useMemo(() => {
    if (!report) return null;
    return {
      ...report.report,
      resources: report.report.resources.map((resource) => ({
        ...resource,
        findings: [],
        // The per-resource verdict goes with them. `analyzeResource` computed
        // it over the findings being dropped here, so leaving it would print
        // "Fix required" beside a row of zeroes in the exported resource table
        // — the same document disagreeing with itself, one column over.
        verdict: verdictFor([]),
        vehicles: resource.vehicles.map((vehicle) => ({ ...vehicle, findings: [] })),
      })),
      // `DEMO_INPUT_NOTE` joins the set rather than being rendered beside it,
      // which is the whole mechanism: one line puts the sentence in the findings
      // panel *and* in the Markdown, the CSV and the JSON, with no export code
      // knowing a demo exists. A sample report that reads as an audit of the
      // reader's own server is the failure this feature would otherwise ship.
      skipped: [
        ...new Set([
          ...report.report.skipped,
          ...(scan?.notices ?? []),
          CLASH_SCOPE_NOTE,
          ...(demo ? [DEMO_INPUT_NOTE] : []),
        ]),
      ],
    };
  }, [report, scan, demo]);

  /**
   * The name a download takes.
   *
   * Prefixed while a demo is loaded, because the notice inside a file is read
   * only by somebody who opens it and the name is read by somebody scrolling a
   * downloads folder six weeks later.
   */
  const exportName = useCallback(
    (name: string) => (demo ? demoDownloadName(name) : name),
    [demo],
  );

  const exportOptions = useMemo(
    () => ({
      title: CLASH_FINDER_TOOL,
      ...(scan?.vanillaNames ? { vanillaNames: scan.vanillaNames } : {}),
    }),
    [scan],
  );

  // ── Filtering, which changes what is drawn and never what is counted ───────

  const needle = normalizeName(query.trim());
  const contests = useMemo(() => {
    if (!report) return [] as Clash[];
    if (needle === "") return report.clashes;
    return report.clashes.filter(
      (clash) =>
        normalizeName(clash.title).includes(needle) ||
        normalizeName(clash.key).includes(needle) ||
        clash.copies.some((copy) => normalizeName(copy.label).includes(needle)),
    );
  }, [report, needle]);

  // ── The byte pass ─────────────────────────────────────────────────────────

  /**
   * The second pass, and the rule that its answers may only land on the drop
   * they were computed from.
   *
   * Every `setBytePass` below is guarded on this run still being the current
   * one, and that is not defensive tidiness — it is the difference between the
   * tool's headline promise and the competitor's answer. `begin` aborts an
   * in-flight pass on a re-pick, but an abort does not unwind an `await`, so
   * the old run still reaches its completion and would write the OLD drop's
   * digests into the new drop's state. `findClashes` matches a digest back by
   * identity plus path, and those are identical when a user re-picks the same
   * `resources/` folder after editing a pack — so the page would print
   * "identical copies, harmless", in green, over two files it never read.
   */
  const runBytePass = useCallback(async () => {
    if (!scan || candidates.length === 0) return;
    hashAbort.current?.abort();
    const controller = new AbortController();
    hashAbort.current = controller;
    const current = () => hashAbort.current === controller && !controller.signal.aborted;
    setBytePass({ kind: "running", done: 0, total: candidates.length });

    const { digests, notes } = await hashContestedFiles(scan, candidates, {
      signal: controller.signal,
      onProgress: (done) => {
        if (done % PROGRESS_EVERY !== 0 || !current()) return;
        setBytePass({ kind: "running", done, total: candidates.length });
      },
    });

    // A pass the user stopped by hand is still this drop's pass and still lands
    // its partial digests — `hashAbort.current` is only cleared by `begin`, and
    // Stop deliberately leaves it pointing at this controller.
    if (hashAbort.current !== controller) return;

    setBytePass({
      kind: "done",
      digests,
      notes,
      hashed: digests.filter((entry) => entry.digest !== null).length,
      total: candidates.length,
    });
  }, [scan, candidates]);

  // ── Gestures ──────────────────────────────────────────────────────────────

  const onFolder = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // A picked `resources/` folder is usually all loose files, whose handles
    // already carry their bytes — but a half-unpacked one holds the zips the
    // user has not extracted yet, and those are the ones with no bytes in them.
    const picked = Array.from(files);
    void begin((signal) => fromDirectoryPick(files, { signal }), picked);
  };
  const onArchives = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    void begin((signal) => fromArchives(picked, { signal }), picked);
  };
  const onDrop = (dt: DataTransferLike | null) => {
    setDragging(false);
    if (!dt) return;
    // A folder drag has an empty `dataTransfer.files` and needs none of these;
    // a zip drag puts the archives there, and those are the handles the byte
    // pass goes back to.
    const dropped = Array.from((dt.files ?? []) as ArrayLike<File>);
    void begin((signal) => fromDataTransfer(dt, { signal }), dropped);
  };

  /**
   * A sample pack, fed through the gesture its registration names.
   *
   * `fromArchives` and not a shortcut into `findClashes`: origin separation, the
   * `truncated` list and the per-archive path namespacing are properties of
   * intake, and a demo that skipped it would be a second code path that can pass
   * while the real one is broken. On this tool it is also the only gesture that
   * can honestly earn "identical" — the byte pass goes back into the archives
   * for the contested entries and hashes what it gets.
   *
   * The tour opens *after* the read resolves, so its first result step has a
   * real contest card to point at rather than an empty page.
   */
  const demoPacks = useMemo(() => demoPacksFor("clash-finder"), []);
  const loadDemo = useCallback(
    async (pack: DemoPack, use: DemoPackUse) => {
      if (use.gesture === "archives") {
        const files = await pack.archives();
        await begin((signal) => fromArchives(files, { signal }), files, true);
      } else {
        const list = pack.pick();
        await begin((signal) => fromDirectoryPick(list, { signal }), Array.from(list), true);
      }
      setTourSignal((signal) => signal + 1);
    },
    [begin],
  );

  return (
    <div className="space-y-8">
      {/* ── Intake ──────────────────────────────────────────────────────── */}
      <section
        data-testid="clash-finder-drop"
        data-walkthrough="clf-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDrop(event.dataTransfer as unknown as DataTransferLike);
        }}
        className={`not-prose rounded-2xl border-2 border-dashed p-5 transition-colors sm:p-6 ${
          dragging ? "border-primary/60 bg-primary/5" : "border-border/70 bg-muted/30"
        }`}
      >
        <h2 className="text-base font-semibold text-foreground">
          Drop your whole <code className="font-mono">resources/</code> folder, or the zips you
          bought
        </h2>
        {/*
          Two claims, because this is the paragraph read before anything is
          dropped: what gets opened, and what leaves the tab.
          The fetch is part of the second claim rather than a sentence after it.
          It is the one exception to "nothing is uploaded" and therefore the one
          part of this paragraph that may not be shortened away — a privacy
          sentence with an unstated exception is the claim a reader is invited to
          check, failing the check.
          Gone: "that is the case this tool exists for", which is this page
          arguing against the prior art in front of somebody trying to use it.
          The argument is point 1 of the header, where the maintainer who can act
          on it will find it, and the long form of the rest is in the page's own
          FAQ below the tool.
        */}
        <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
          Only text files are opened, each up to 4 MB; binary assets are counted by name and size
          and read only if they collide.{" "}
          <strong className="text-foreground">Nothing you drop is uploaded</strong> — except that
          the page fetches our own base-game name list.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
            <FolderOpen className="h-4 w-4" />
            Pick a folder
            <input
              data-testid="clash-finder-folder"
              type="file"
              multiple
              className="sr-only"
              onChange={(event) => onFolder(event.target.files)}
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40">
            <Upload className="h-4 w-4" />
            Pick zips
            <input
              data-testid="clash-finder-archives"
              type="file"
              multiple
              accept=".zip,.rar,.7z"
              className="sr-only"
              onChange={(event) => onArchives(event.target.files)}
            />
          </label>
          {/* In the intake row and gated on nothing — see `ShowTourButton`.
              The sample-pack panel below is gone the moment a report exists, so
              it cannot be the only route to the tour, and the reader who
              dropped their own folder is the one who needed it most. */}
          <ShowTourButton
            testId="clash-finder-show-tour"
            onClick={() => setTourSignal((signal) => signal + 1)}
          />
        </div>

        {/* ── The worked example, in the empty state only ──
            One button per registered use, labelled with what it teaches rather
            than with "Load demo", because a reader deciding whether to press it
            is deciding whether they want to see that finding.

            They stop being rendered the moment a scan exists, which is honesty
            rule 3 with no hidden state in it: a demo may never replace input the
            user loaded, and a button that is not on the page cannot. There is
            deliberately no confirm dialog — the rule is the absence of the
            control, not a question about it. */}
        {phase.kind !== "ready" && demoPacks.length > 0 && (
          <div className="mt-4 rounded-xl border border-border/60 bg-background/60 p-4">
            <p className="text-xs font-medium text-foreground">
              No folder to hand? Load a sample pack and see what a real collision looks like.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {demoPacks.map(({ pack, use }) => (
                <button
                  key={pack.id}
                  type="button"
                  data-testid="clash-finder-demo"
                  disabled={phase.kind === "reading"}
                  onClick={() => void loadDemo(pack, use)}
                  className="max-w-full rounded-lg border border-border bg-card px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40 disabled:opacity-50"
                >
                  <span className="block text-foreground">{pack.label}</span>
                  <span className="mt-0.5 block font-normal leading-5 text-muted-foreground">
                    {use.teaches}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase.kind === "reading" && (
          <p className="mt-4 text-sm text-muted-foreground">Reading the folder…</p>
        )}
        {phase.kind === "failed" && (
          <p className="mt-4 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>That folder could not be read: {phase.message}. Nothing was scanned.</span>
          </p>
        )}
        {/* The badge, beside the count rather than at the top of the page,
            because this is where a reader looks to find out what was scanned.
            Never behind a disclosure and never a tooltip: a sentence a reader
            has to open something to see has not been told. */}
        {scan && demo && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span
              data-testid="clash-finder-demo-state"
              className="inline-flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-1.5 text-xs font-medium text-foreground"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
              {DEMO_STATE_LABEL}
            </span>
          </div>
        )}
        {scan && (
          <p className="mt-4 text-xs leading-6 text-muted-foreground">
            {count(scan.files, "file")} seen, {formatSize(scan.totalBytes)} in total.{" "}
            {count(scan.opened, "of them was", "of them were")} opened and decoded as text; the rest
            were listed by name and size. Anything the reader refused is in the caveats below.
          </p>
        )}
      </section>

      {/* ── The config, which turns "these collide" into "this one wins" ── */}
      <section
        aria-label="server.cfg"
        data-walkthrough="clf-cfg"
        className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5"
      >
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <FileCog className="h-4 w-4 text-primary/80" />
          Your <code className="font-mono">server.cfg</code> — optional, and it changes the answer
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
          Start order is the only thing that decides which copy of a colliding asset the game keeps.
          Without it this tool says <em className="text-foreground">{LOAD_ORDER_UNKNOWN}</em> on
          every collision. Paste the config, or pick the file — it is read in this tab like
          everything else.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30">
            <Upload className="h-3.5 w-3.5" />
            Pick server.cfg
            <input
              data-testid="clash-finder-cfg-file"
              type="file"
              accept=".cfg,.txt"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                setCfgName(file.name);
                void file.text().then(setCfgText);
              }}
            />
          </label>
          {cfg && (
            <span className="text-xs text-muted-foreground" data-testid="clash-finder-cfg-state">
              {cfgName}: {count(cfg.order.length, "ensure line")} read
              {cfg.categories.size > 0
                ? `, plus ${count(cfg.categories.size, "bracket folder")} started as a unit`
                : ""}
              .
            </span>
          )}
        </div>

        <textarea
          data-testid="clash-finder-cfg-text"
          value={cfgText}
          onChange={(event) => setCfgText(event.target.value)}
          spellCheck={false}
          rows={4}
          placeholder={"ensure ox_lib\nensure vendor-a-police\nensure vendor-b-police"}
          className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs leading-6 text-foreground outline-none transition-colors focus:border-primary/50"
        />

        {report && report.cfg.caveats.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="clash-finder-cfg-caveats">
            {report.cfg.caveats.map((text) => (
              <li key={text} className="flex gap-2 text-xs leading-6 text-muted-foreground">
                <AlertTriangle className="mt-1.5 h-3 w-3 shrink-0 text-warning" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {report && scan && (
        <>
          {/* ── The figures. Every one counts every row. ────────────────── */}
          <section aria-label="Totals" className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Figure label="Resources" value={n(report.counts.resources)} />
            <Figure
              label="Collisions"
              value={n(report.counts.clashes)}
              tone={report.counts.clashes > 0 ? "bad" : "plain"}
            />
            <Figure label="Stream names" value={n(report.counts.stream)} />
            <Figure label="Spawn codes" value={n(report.counts.spawn)} />
            <Figure label="data_file" value={n(report.counts.dataFile)} />
            {/* Zero winners named without a config is not "no winner exists",
                it is "nothing here could decide" — the same distinction
                `VERDICT_LABELS.unknown` draws for the byte pass. The warn tone
                says something is off; the note says what, on the tile, where
                the number is. */}
            <Figure
              label="Winners named"
              value={n(report.counts.resolvedWinners)}
              tone={report.cfg.supplied ? "plain" : "warn"}
              {...(report.cfg.supplied ? {} : { note: "needs a server.cfg" })}
            />
          </section>

          {/* ── Pass two, which is the user's to spend ──────────────────── */}
          {candidates.length > 0 && (
            <section
              aria-label="Identical or different"
              data-testid="clash-finder-byte-pass"
              data-walkthrough="clf-bytes"
              className="rounded-2xl border border-border/60 bg-muted/30 p-4 sm:p-5"
            >
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <Fingerprint className="h-4 w-4 text-primary/80" />
                Are the colliding copies the same file?
              </h2>
              {/*
                Two sentences, because the button below is the third. It reads
                `Compare the bytes (12 files, 4.2 MB)` — the same two numbers
                this paragraph used to state twelve lines above the place the
                reader clicks, which is one fact on one screen twice.
                What a collision *means* — a vendor's own shared wheel costs you
                nothing, a genuine override means something you paid for never
                appears — moved into `KIND_MEANINGS`, the legend that exists to
                explain collision kinds. It is not deleted and it is one click
                from here.
              */}
              <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
                The names above were compared without reading a byte. Whether a collision matters
                depends on whether the copies are the same file.
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                {bytePass.kind === "not-run" && (
                  <>
                    <button
                      type="button"
                      onClick={() => void runBytePass()}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      <Fingerprint className="h-4 w-4" />
                      Compare the bytes ({count(candidates.length, "file")},{" "}
                      {formatSize(contestedBytes)})
                    </button>
                    <button
                      type="button"
                      onClick={() => setBytePass({ kind: "declined" })}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                    >
                      Skip it
                    </button>
                    <span className="text-xs text-warning" data-testid="clash-finder-byte-state">
                      Until this runs, every collision below says{" "}
                      <strong>{VERDICT_LABELS.unknown}</strong>.
                    </span>
                  </>
                )}

                {bytePass.kind === "running" && (
                  <>
                    <span className="text-sm text-foreground" data-testid="clash-finder-byte-state">
                      Hashing {n(bytePass.done)} of {n(bytePass.total)} contested files…
                    </span>
                    <button
                      type="button"
                      onClick={() => hashAbort.current?.abort()}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                    >
                      Stop
                    </button>
                  </>
                )}

                {bytePass.kind === "done" && (
                  <>
                    <span
                      className="text-sm text-muted-foreground"
                      data-testid="clash-finder-byte-state"
                    >
                      {n(bytePass.hashed)} of {n(bytePass.total)} contested files were read and
                      hashed. {n(report.counts.identical)} identical, {n(report.counts.different)}{" "}
                      {/*
                        `unknownStream`, not `unknownContent`: a spawn code and
                        a data_file registration have no bytes and are `unknown`
                        by construction, so counting them here told a reader
                        whose comparison completed in full that five of six
                        things went unread. `clash.ts` applies the same filter
                        to the note in the report's footer.
                      */}
                      different, {n(report.counts.unknownStream)} not compared.
                    </span>
                    {bytePass.hashed < bytePass.total && (
                      // Stopping, or hitting the library's file and byte
                      // ceilings, must not be a one-way door: the only other
                      // route back to a full comparison is re-picking the
                      // folder, which re-runs intake and the whole analysis
                      // over every resource to redo the one cheap part.
                      <button
                        type="button"
                        onClick={() => void runBytePass()}
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                      >
                        <Fingerprint className="h-3.5 w-3.5" />
                        Run it again for the rest
                      </button>
                    )}
                  </>
                )}

                {bytePass.kind === "declined" && (
                  <>
                    <span className="text-sm text-warning" data-testid="clash-finder-byte-state">
                      Skipped — every collision below says {VERDICT_LABELS.unknown}, and none of
                      them is called harmless.
                    </span>
                    <button
                      type="button"
                      onClick={() => void runBytePass()}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                    >
                      <Fingerprint className="h-3.5 w-3.5" />
                      Run it after all
                    </button>
                  </>
                )}
              </div>

              {bytePass.kind === "done" && bytePass.notes.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="clash-finder-byte-notes">
                  {bytePass.notes.map((text) => (
                    <li key={text} className="flex gap-2 text-xs leading-6 text-muted-foreground">
                      <AlertTriangle className="mt-1.5 h-3 w-3 shrink-0 text-warning" />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* ── Exports ─────────────────────────────────────────────────── */}
          <section
            aria-label="Exports"
            data-walkthrough="clf-exports"
            className="flex flex-wrap gap-2"
          >
            <ExportButton
              icon={FileText}
              label="Markdown (.md)"
              onClick={() =>
                exported &&
                downloadTextFile(
                  exportName("clash-report.md"),
                  toMarkdown(exported, exportOptions),
                  "text/markdown",
                )
              }
            />
            <ExportButton
              icon={FileSpreadsheet}
              label="Spreadsheet (.csv)"
              onClick={() =>
                exported &&
                downloadTextFile(
                  exportName("clash-report.csv"),
                  toCsv(exported, exportOptions),
                  "text/csv",
                )
              }
            />
            <ExportButton
              icon={FileJson}
              label="JSON"
              onClick={() =>
                exported &&
                downloadTextFile(
                  exportName("clash-report.json"),
                  toJson(exported, exportOptions),
                  "application/json",
                )
              }
            />
          </section>

          {/* ── Findings, through the shared surface ────────────────────── */}
          {/*
            `report` as well as `findings`: the findings are what is ranked and
            drawn, and the report is what gives this panel the skipped-checks
            line, the resource attribution and the copy-as-Markdown button.

            And it is `exported`, not `report.report`, precisely so the two
            cannot disagree: the panel's own Copy-as-Markdown runs `toMarkdown`
            over whatever it was handed, so handing it the un-emptied report
            gave a paste that listed findings the panel never drew and omitted
            the scope note the three buttons carry. One value, one document.

            `exported.skipped` carries `CLASH_SCOPE_NOTE`, and this panel prints
            `skipped` under `Not checked:` — which is why the note no longer also
            renders as a paragraph under the totals grid. It is a claim about the
            whole scan, so it lives in the one place this layer keeps scan-level
            honesty, in words, on screen, beside everything else the read
            refused. Deleting the paragraph took nothing off the page; it stopped
            saying the same thing in two registers, one of which looked like
            explanation.

            The `emptyLabel` lost its second sentence for the same reason: the
            paragraph under the contest list says the same thing forty words
            later and says it better, because it also carries "That is a
            statement about the checks that ran".
          */}
          <section aria-label="Problems" data-walkthrough="clf-findings">
            <FindingsList
              findings={report.findings}
              report={exported ?? report.report}
              truncated={scan.notices}
              emptyLabel="No collision was found in the checks that ran."
            />
          </section>

          {/* ── The contests, one card per contested name ───────────────── */}
          <section aria-label="Collisions" data-walkthrough="clf-contests" className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-foreground">
                  What is being fought over
                </h2>
                <p className="mt-1 text-xs text-muted-foreground" data-testid="clash-finder-scope">
                  {needle === ""
                    ? `${count(report.counts.clashes, "collision")} across ${count(report.counts.resources, "resource")}, worst first, emergency fleet above everything else.`
                    : `${n(contests.length)} of ${count(report.counts.clashes, "collision")} match “${query.trim()}”.`}
                </p>
              </div>
              <label className="text-xs text-muted-foreground">
                <span className="sr-only">Find a name or a resource</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setShown(CLASH_VISIBLE);
                  }}
                  placeholder="Find a name or a resource"
                  spellCheck={false}
                  className="w-64 max-w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground outline-none transition-colors focus:border-primary/50"
                />
              </label>
            </div>

            <KindLegend />

            {contests.slice(0, shown).map((clash) => (
              <ContestCard
                key={`${clash.kind}0000${clash.key}`}
                clash={clash}
                shown={copiesShown[`${clash.kind}0000${clash.key}`] ?? CLASH_COPIES_VISIBLE}
                onMore={() =>
                  setCopiesShown((current) => {
                    const key = `${clash.kind}0000${clash.key}`;
                    return {
                      ...current,
                      [key]: (current[key] ?? CLASH_COPIES_VISIBLE) + CLASH_COPIES_PAGE,
                    };
                  })
                }
              />
            ))}

            {contests.length > shown && (
              <button
                type="button"
                onClick={() => setShown((current) => current + CLASH_PAGE)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30"
              >
                <ChevronRight className="h-4 w-4" />
                Show {n(Math.min(contests.length - shown, CLASH_PAGE))} more of{" "}
                {count(contests.length, "collision")}
              </button>
            )}

            {contests.length === 0 && report.counts.clashes > 0 && (
              <p className="text-sm text-muted-foreground">
                No collision matches “{query.trim()}”. Every one of the{" "}
                {count(report.counts.clashes, "collision")} found is still in the exports.
              </p>
            )}

            {report.counts.clashes === 0 && report.counts.resources >= 2 && (
              <p className="text-sm text-muted-foreground">
                Nothing in this drop is contested: no streamed name, no spawn code and no{" "}
                <code className="font-mono">data_file</code> registration is claimed by two of the{" "}
                {count(report.counts.resources, "resource")} in it. That is a statement about the
                checks that ran — read the not-checked line above for the ones that did not.
              </p>
            )}

            {/*
              The one input on which nothing on this page could fire. Every rule
              in `clash.ts` needs two owners of one name, so a drop of a single
              pack produces "0 Collisions" over a scan in which no check ran —
              and the reader's only signal is a `1` in a totals tile they have
              to interpret. The same sentence is in the report's `skipped`, so
              it reaches the exports too.
            */}
            {report.counts.resources < 2 && (
              <p className="text-sm text-warning" data-testid="clash-finder-too-few">
                This drop holds{" "}
                {report.counts.resources === 1 ? "one resource" : "no resource at all"}. Every check
                on this page compares one resource against another, so none of them could fire —
                this is not a clean result, it is an empty one. Drop your whole{" "}
                <code className="font-mono">resources/</code> folder, or the packs you want compared
                together.
              </p>
            )}
          </section>

          {/* ── Housekeeping, kept apart on purpose ─────────────────────── */}
          {/*
            Why apart: nothing in here is being shadowed by another vendor, and
            painting it the same red as a real collision is how a reader learns
            to skip the red. That argument used to be on screen, addressed to a
            user who was reading a list of duplicates and could do nothing with
            it. It is a maintainer's reason and it belongs here; what the reader
            keeps is the fact — housekeeping, not a fight between packs, and in
            every export either way.
          */}
          {report.housekeeping.length > 0 && (
            <section aria-label="Housekeeping" className="space-y-3">
              <h2 className="text-xl font-semibold tracking-tight text-foreground">
                Inside one resource ({n(report.housekeeping.length)})
              </h2>
              <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
                One resource shipping the same streamed name twice. FiveM does complain about this
                and it is worth fixing, but it is housekeeping rather than a fight between packs —
                nothing here is being shadowed by another vendor. These are the same findings the
                panel above ranks, so no export loses them.
              </p>
              <ul className="divide-y divide-border/50 rounded-2xl border border-border/60 bg-card">
                {report.housekeeping.slice(0, housekeepingShown).map((finding) => (
                  <li
                    key={`${finding.code}0000${finding.entity?.id ?? finding.title}`}
                    data-testid="clash-finder-housekeeping"
                    className="p-4"
                  >
                    <p className="text-sm font-medium text-foreground">{finding.title}</p>
                    <p className="mt-1 text-xs leading-6 text-muted-foreground">{finding.detail}</p>
                  </li>
                ))}
              </ul>
              {report.housekeeping.length > housekeepingShown && (
                <button
                  type="button"
                  onClick={() => setHousekeepingShown((current) => current + HOUSEKEEPING_PAGE)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30"
                >
                  <ChevronRight className="h-4 w-4" />
                  Show{" "}
                  {n(Math.min(report.housekeeping.length - housekeepingShown, HOUSEKEEPING_PAGE))}{" "}
                  more of {n(report.housekeeping.length)}
                </button>
              )}
            </section>
          )}
        </>
      )}

      {/* The tour, in manual mode.

          `openSignal` and not `enabled={hasReport}`: flipping `enabled` true
          would start the tour for anybody whose report had just appeared,
          including the first-time visitor who ignored the demo buttons and
          dropped their own 300-resource folder. That user gets a dimmer over
          their own results and no way to know what asked for it. The signal is
          raised by the demo button and by the replay button beside the badge,
          both of which are a person saying "teach me". */}
      <ToolWalkthrough
        /* The sample-announcing first step is dropped when no sample is
           loaded, and this is an honesty fix rather than a tidy-up. That step
           says "not your files" — true over a demo, and a straightforward
           falsehood over a folder the reader picked themselves, which the
           permanent "Show me how this works" control now makes reachable.
           Filtered by `id` and not by index so reordering the array cannot
           silently drop a different step. Nothing is lost on the demo path,
           and nothing is claimed on the real one. */
        steps={demo ? clashFinderSteps : clashFinderSteps.filter((step) => step.id !== "welcome")}
        storageKey="workshop_tour_clash_finder_v1"
        finishLabel={clashFinderFinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

/**
 * One contested name: who is claiming it, whether the copies differ, and which
 * one is in game today.
 *
 * Every sentence here came out of `clash.ts`. The card decides nothing — it
 * does not re-rank the copies, does not infer a winner from their order and
 * does not shorten a reason, because the reason is the argument the reader is
 * being asked to accept and a summary of it is a claim this page would then own.
 */
function ContestCard({
  clash,
  shown,
  onMore,
}: {
  clash: Clash;
  shown: number;
  onMore: () => void;
}) {
  const remaining = clash.copies.length - shown;
  return (
    <article
      data-testid="clash-finder-contest"
      className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5"
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="font-mono text-sm font-semibold text-foreground">{clash.title}</h3>
        <Chip>{KIND_LABELS[clash.kind]}</Chip>
        <Chip
          tone={
            clash.content.verdict === "identical"
              ? "good"
              : clash.content.verdict === "different"
                ? "bad"
                : "warn"
          }
        >
          {VERDICT_LABELS[clash.content.verdict]}
        </Chip>
        {clash.emergency && <Chip tone="bad">emergency fleet</Chip>}
        {clash.vanilla && <Chip tone="warn">base-game name</Chip>}
        <span className="text-xs text-muted-foreground">
          {count(clash.copies.length, "resource")} claiming it
        </span>
      </header>

      <p className="mt-2 text-xs leading-6 text-muted-foreground">{clash.content.reason}</p>

      <p className="mt-2 text-xs leading-6">
        <strong className={clash.winner.resolved ? "text-foreground" : "text-warning"}>
          {clash.winner.resolved ? `In game today: ${clash.winner.label}. ` : ""}
        </strong>
        <span className="text-muted-foreground">{clash.winner.reason}</span>
      </p>

      {clash.winner.caveats.length > 0 && (
        <ul className="mt-2 space-y-1">
          {clash.winner.caveats.map((text) => (
            <li key={text} className="flex gap-2 text-xs leading-6 text-muted-foreground">
              <AlertTriangle className="mt-1.5 h-3 w-3 shrink-0 text-warning" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      )}

      <ul className="mt-3 divide-y divide-border/50">
        {clash.copies.slice(0, shown).map((copy) => {
          const wins = clash.winner.resolved && clash.winner.identity === copy.identity;
          return (
            <li
              key={`${copy.identity}0000${copy.path}`}
              data-testid="clash-finder-copy"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
            >
              <Chip tone={wins ? "good" : "plain"}>{copy.label}</Chip>
              {/* The full path, never truncated: it is the cell the reader has
                  to open a file manager with, and it is why this page is wide. */}
              <code className="min-w-0 break-all font-mono text-xs text-foreground">
                {copy.path}
              </code>
              <span className="ml-auto flex shrink-0 items-center gap-3 text-[11px] text-muted-foreground">
                {copy.origin !== null && <span>from {copy.origin}</span>}
                {copy.size > 0 && <span>{formatSize(copy.size)}</span>}
                {copy.startPosition !== null && <span>#{n(copy.startPosition + 1)} in the order</span>}
                {wins && <span className="font-medium text-success">wins</span>}
              </span>
            </li>
          );
        })}
      </ul>

      {remaining > 0 && (
        <button
          type="button"
          onClick={onMore}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
        >
          <ChevronRight className="h-3.5 w-3.5" />
          Show {n(Math.min(remaining, CLASH_COPIES_PAGE))} more of{" "}
          {count(clash.copies.length, "copy", "copies")}
        </button>
      )}
    </article>
  );
}

/**
 * One figure, and the caveat that belongs to that figure rather than to the page.
 *
 * `note` is what this gained, and the argument is the one `VERDICT_LABELS`
 * makes one screen away: a number nobody could earn has to say so where the
 * number is. "0 winners named" is a property of the input — no config — and not
 * a property of the collisions, and a reader who takes it for the second reads
 * the whole report backwards. A paragraph under the grid cannot say which tile
 * it is about; a caption inside the tile is read by anyone who reads the number.
 *
 * Six words is the budget. A caption that wraps is the paragraph again, and a
 * tooltip is worse than either — an honesty claim behind a hover does not exist
 * on a touch device.
 */
function Figure({
  label,
  value,
  tone = "plain",
  note,
}: {
  label: string;
  value: string;
  tone?: "plain" | "warn" | "bad";
  note?: string;
}) {
  const colour =
    tone === "bad" ? "text-destructive" : tone === "warn" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
      <div className={`font-mono text-xl font-semibold ${colour}`}>{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {note !== undefined && <div className="mt-0.5 text-[11px] text-muted-foreground">{note}</div>}
    </div>
  );
}

function Chip({
  children,
  tone = "plain",
}: {
  children: ReactNode;
  tone?: "plain" | "good" | "warn" | "bad";
}) {
  const colour =
    tone === "bad"
      ? "border-destructive/40 text-destructive"
      : tone === "warn"
        ? "border-warning/40 text-warning"
        : tone === "good"
          ? "border-success/40 text-success"
          : "border-border text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] leading-5 ${colour}`}
    >
      {children}
    </span>
  );
}

function ExportButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Download;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40"
    >
      <Icon className="h-4 w-4 text-primary/80" />
      {label}
    </button>
  );
}

/**
 * What the three kinds of collision are, and which end of the start order
 * settles each.
 *
 * Said once rather than repeated on every card: the asymmetry — a streamed file
 * is won by the resource that starts *last*, a spawn code by the one that
 * starts *first* — is the single thing a reader of this page most needs and
 * least expects, and a tool that applied one rule to both would be confidently
 * wrong about half of its own findings.
 */
function KindLegend() {
  const kinds = Object.keys(KIND_LABELS) as ClashKind[];
  return (
    <details className="group rounded-xl border border-border/60 bg-muted/30 px-4 py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs font-medium text-foreground [&::-webkit-details-marker]:hidden">
        <span>What the three kinds of collision are</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>
      <dl className="mt-3 space-y-2">
        {kinds.map((kind) => (
          <div key={kind} className="flex flex-wrap items-baseline gap-2">
            <dt>
              <Chip>{KIND_LABELS[kind]}</Chip>
            </dt>
            <dd className="flex-1 text-xs leading-6 text-muted-foreground">
              {KIND_MEANINGS[kind]}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
